import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { AssignedFi, PipelineColumnDef, PipelineRow, STATUS_BADGE, OUTCOME_BADGE } from '../adminData';

// Cohorts available in the DB: live FY27 down through the seeded historical
// pipeline (FY18 had no pipeline export, so it's omitted).
const YEARS = ['FY27', 'FY26', 'FY25', 'FY24', 'FY23', 'FY22', 'FY21', 'FY20', 'FY19', 'FY17'];

// Score cell formatter: two decimals, or an em-dash when there's no score.
const fmtScore = (v: number | null | undefined): string =>
  v != null ? v.toFixed(2) : '—';

export default function InternalPipeline() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [exported, setExported] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');

  const { data: settings } = useApi<any>(() => API.getSettings(), []);
  useEffect(() => {
    if (selectedYear || !settings?.currentYear) return;
    const fy = `FY${String(settings.currentYear).slice(-2)}`;
    setSelectedYear(YEARS.includes(fy) ? fy : YEARS[0]);
  }, [settings]);
  // Column filters ('' = all). colFilters is keyed by answer-column key (a
  // question id for 2027+, or 'industry' for the legacy layout). openMenu holds
  // the key of the expanded dropdown ('status' or `col:<key>`).
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Sort state: key identifies the column, dir toggles asc/desc.
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Row whose FI assignments are being edited (null = modal closed).
  const [editing, setEditing] = useState<PipelineRow | null>(null);
  // Assignment edits applied since load, keyed by app id — lets the table reflect
  // saves without refetching (useApi has no refetch). Cleared on year change.
  const [overrides, setOverrides] = useState<Record<string, AssignedFi[]>>({});
  // Archive/unarchive applied since load, keyed by app id (same no-refetch reason).
  const [archivedOverrides, setArchivedOverrides] = useState<Record<string, boolean>>({});
  // Archived-only filter: off (default) shows active applications and hides
  // archived ones; on shows *only* archived applications.
  const [archivedOnly, setArchivedOnly] = useState(false);
  // App id whose row action (⋯) menu is open.
  const [rowMenu, setRowMenu] = useState<string | null>(null);

  const { data, loading, error } = useApi(
    () => API.internalApplications(selectedYear, archivedOnly),
    [selectedYear, archivedOnly],
  );
  // FIs available as evaluators for the selected cohort (the assignment picker).
  const { data: fis } = useApi(() => API.listFIs(selectedYear), [selectedYear]);

  const selectYear = (year: string) => {
    setSelectedYear(year);
    setSearch('');
    setExported(false);
    setOverrides({});
    setArchivedOverrides({});
    setArchivedOnly(false);
    setColFilters({});
    setStatusFilter('');
    setOpenMenu(null);
    setRowMenu(null);
    setSortKey(null);
    setSortDir('asc');
  };

  // Effective archived state for a row (local override wins over the fetched value).
  const isArchived = (row: PipelineRow): boolean => archivedOverrides[row.id] ?? row.archived;

  const toggleArchived = (row: PipelineRow, archived: boolean) => {
    setRowMenu(null);
    setArchivedOverrides(prev => ({ ...prev, [row.id]: archived }));
    API.setApplicationArchived(row.id, archived).catch(() => {
      // Revert the optimistic override on failure.
      setArchivedOverrides(prev => ({ ...prev, [row.id]: !archived }));
    });
  };

  // Wipe an application back to a blank Draft (answers, files, scores and
  // assignments gone) so the applicant can start over. Irreversible, so it's
  // confirmed first; the reset touches too many row fields to patch locally, so
  // we reload once the server confirms.
  const resetApplication = (row: PipelineRow) => {
    setRowMenu(null);
    const ok = window.confirm(
      `Reset application ${row.id}?\n\nThis permanently deletes all answers, uploaded files, ` +
      `evaluator scores and FI assignments, and returns it to a blank Draft for the applicant. ` +
      `This cannot be undone.`
    );
    if (!ok) return;
    API.resetApplication(row.id)
      .then(() => window.location.reload())
      .catch(() => window.alert('Failed to reset the application. Please try again.'));
  };

  // Permanently delete the applicant's account and everything tied to it — their
  // login, the whole application and all its data. Unlike reset, nothing survives,
  // so it's confirmed first and the row is gone on reload.
  const deleteAccount = (row: PipelineRow) => {
    setRowMenu(null);
    const ok = window.confirm(
      `Delete the applicant account for application ${row.id}?\n\n` +
      `This permanently removes the applicant's login and their entire application ` +
      `(answers, uploaded files, evaluator scores, FI evaluations and assignments). ` +
      `This cannot be undone.`
    );
    if (!ok) return;
    API.deleteApplicationAccount(row.id)
      .then(() => window.location.reload())
      .catch(() => window.alert('Failed to delete the account. Please try again.'));
  };

  const numericSortKeys = new Set(['id', 'avgScore', 'totalAvg', 'fiAvg', 'vcAvg', 'eirAvg', 'dateCompleted', 'lastEdited']);
  const isNumericKey = (key: string) => numericSortKeys.has(key) || key.startsWith('col:');

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(isNumericKey(key) ? 'desc' : 'asc');
    }
  };

  const kpis = data?.kpis;
  const apps: PipelineRow[] = data?.applications ?? [];
  // 2027+ layout: score breakdown + admin-configured answer columns.
  const newLayout: boolean = !!data?.newLayout;
  const columnDefs: PipelineColumnDef[] = data?.columnDefs ?? [];
  const assignedOf = (row: PipelineRow): AssignedFi[] => overrides[row.id] ?? row.assignedFis ?? [];

  // Filterable answer-derived columns, unified across layouts: each configured
  // column (2027+) or the single legacy Industry column gets its own dropdown.
  const filterCols: { key: string; label: string; labelPlural: string; get: (a: PipelineRow) => string }[] =
    newLayout
      ? columnDefs.map(c => ({
          key: String(c.questionId),
          label: c.label,
          labelPlural: c.label,
          get: (a: PipelineRow) => a.dynamicCols?.[String(c.questionId)] ?? '',
        }))
      : [{ key: 'industry', label: 'Industry', labelPlural: 'Industries', get: (a: PipelineRow) => a.industry }];

  const statuses = Array.from(new Set(apps.map(a => a.status).filter(Boolean))).sort();

  const sortGetters: Record<string, (a: PipelineRow) => string | number> = {
    id: a => a.id,
    company: a => a.company,
    status: a => a.status ?? '',
    totalAvg: a => a.totalAvg ?? -Infinity,
    fiAvg: a => a.fiAvg ?? -Infinity,
    vcAvg: a => a.vcAvg ?? -Infinity,
    eirAvg: a => a.eirAvg ?? -Infinity,
    industry: a => a.industry ?? '',
    avgScore: a => a.avgScore ?? -Infinity,
    submitted: a => a.submitted ?? '',
    // Formatted date strings ("Jun 10, 2026"); parse to epoch ms so sorting is
    // chronological. Unset dates ("—") sink to the bottom.
    dateCompleted: a => Date.parse(a.dateCompleted) || -Infinity,
    lastEdited: a => Date.parse(a.lastEdited) || -Infinity,
    outcome: a => a.outcome ?? '',
    ...Object.fromEntries(columnDefs.map(c => [
      `col:${c.questionId}`,
      (a: PipelineRow) => a.dynamicCols?.[String(c.questionId)] ?? '',
    ])),
  };

  const filtered = apps.filter(a =>
    (archivedOnly ? isArchived(a) : !isArchived(a)) &&
    (!search ||
      a.company.toLowerCase().includes(search.toLowerCase()) ||
      a.id.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || a.status === statusFilter) &&
    filterCols.every(fc => !colFilters[fc.key] || fc.get(a) === colFilters[fc.key]),
  );

  const shown = sortKey && sortGetters[sortKey]
    ? [...filtered].sort((a, b) => {
        const va = sortGetters[sortKey](a);
        const vb = sortGetters[sortKey](b);
        const cmp = typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  // Total column count for the empty-state row's colSpan. Both layouts carry the
  // Date Completed + Date last edited pair (2 cols) added below.
  const colCount = newLayout ? 11 + columnDefs.length : 10;

  return (
    <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start', minHeight: '100%' }}>
      {/* Year ribbon */}
      <div style={{
        width: 72,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        paddingTop: 2,
        marginRight: 28,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, paddingLeft: 2 }}>Year</div>
        {YEARS.map(year => {
          const active = year === selectedYear;
          return (
            <button
              key={year}
              onClick={() => selectYear(year)}
              style={{
                width: '100%',
                padding: '8px 0',
                borderRadius: 6,
                border: active ? '2px solid #1a4fa0' : '2px solid transparent',
                background: active ? '#1a4fa0' : '#f0f2f5',
                color: active ? '#fff' : '#444',
                fontWeight: active ? 700 : 500,
                fontSize: 13,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              {year}
            </button>
          );
        })}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="phil-page-heading">Application Pipeline</div>
        <div className="phil-page-sub">{selectedYear} · all submitted and in-progress applications</div>

        {loading && <div className="phil-loading">Loading…</div>}
        {(error || (!loading && !data)) && <div className="phil-error">Unable to load pipeline. {error}</div>}

        {!loading && data && (
          <>
            <div className="phil-kpi-row">
              <div className="phil-kpi-card"><div className="phil-kpi-number">{kpis.total}</div><div className="phil-kpi-label">Total Applications</div></div>
              <div className="phil-kpi-card"><div className="phil-kpi-number">{kpis.submitted}</div><div className="phil-kpi-label">Submitted</div></div>
              <div className="phil-kpi-card"><div className="phil-kpi-number">{kpis.inProgress}</div><div className="phil-kpi-label">In Progress</div></div>

            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <input
                className="search-input"
                style={{ maxWidth: 320 }}
                placeholder="🔍 Search by company name or ID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {filterCols.map(fc => {
                const menuKey = `col:${fc.key}`;
                return (
                  <FilterDropdown
                    key={fc.key}
                    label={fc.label}
                    labelPlural={fc.labelPlural}
                    value={colFilters[fc.key] ?? ''}
                    options={Array.from(new Set(apps.map(fc.get).filter(Boolean))).sort()}
                    open={openMenu === menuKey}
                    onToggle={() => setOpenMenu(openMenu === menuKey ? null : menuKey)}
                    onSelect={v => { setColFilters(prev => ({ ...prev, [fc.key]: v })); setOpenMenu(null); }}
                  />
                );
              })}
              <FilterDropdown
                label="Status"
                labelPlural="Statuses"
                value={statusFilter}
                options={statuses}
                open={openMenu === 'status'}
                onToggle={() => setOpenMenu(openMenu === 'status' ? null : 'status')}
                onSelect={v => { setStatusFilter(v); setOpenMenu(null); }}
              />
              <button
                className={`phil-btn phil-btn-sm ${archivedOnly ? 'phil-btn-primary' : 'phil-btn-secondary'}`}
                onClick={() => setArchivedOnly(v => !v)}
                title="Show only archived applications"
              >
                {archivedOnly ? '✓ ' : ''}Archived only
              </button>
              <button
                className={`phil-btn phil-btn-sm ${exported ? 'phil-btn-secondary' : 'phil-btn-primary'}`}
                style={{ marginLeft: 'auto' }}
                onClick={() => { API.exportApplications({ cycle: selectedYear }).catch(() => {}); setExported(true); }}
              >
                {exported ? '✓ Exported' : '⬇ Export to Excel'}
              </button>
            </div>

            <div className="phil-card">
              <table className="phil-table">
                <thead>
                  {newLayout ? (
                    <tr>
                      <SortTh label="App ID" sortKey="id" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Company" sortKey="company" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      {columnDefs.map(c => (
                        <SortTh key={c.questionId} label={c.label} sortKey={`col:${c.questionId}`} current={sortKey} dir={sortDir} onSort={toggleSort} />
                      ))}
                      <SortTh label="Total Avg Score" sortKey="totalAvg" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Total Avg FI" sortKey="fiAvg" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Total Avg VC" sortKey="vcAvg" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Total Avg EIR/FIL" sortKey="eirAvg" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Date Completed" sortKey="dateCompleted" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Date last edited" sortKey="lastEdited" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <th>Assigned Evaluator</th>
                      <th></th>
                    </tr>
                  ) : (
                    <tr>
                      <SortTh label="App ID" sortKey="id" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Company" sortKey="company" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Industry" sortKey="industry" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Avg Score" sortKey="avgScore" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Date Completed" sortKey="dateCompleted" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Date last edited" sortKey="lastEdited" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Outcome" sortKey="outcome" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortTh label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                      <th>Assigned FIs</th>
                      <th></th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {shown.map(a => {
                    const assigned = assignedOf(a);
                    return (
                    <tr key={a.id} className="phil-row-link" onClick={() => navigate(`/admin/applications/${a.id}`)}>
                      <td style={{ fontFamily: 'monospace', color: '#888', fontSize: 12 }}>{a.id}</td>
                      <td style={{ fontWeight: 600 }}>{a.company}</td>
                      {newLayout ? (
                        <>
                          {columnDefs.map(c => (
                            <td key={c.questionId}>{a.dynamicCols?.[String(c.questionId)] ?? '—'}</td>
                          ))}
                          <td style={{ color: '#666' }}>{fmtScore(a.totalAvg)}</td>
                          <td style={{ color: '#666' }}>{fmtScore(a.fiAvg)}</td>
                          <td style={{ color: '#666' }}>{fmtScore(a.vcAvg)}</td>
                          <td style={{ color: '#666' }}>{fmtScore(a.eirAvg)}</td>
                          <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{a.dateCompleted}</td>
                          <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{a.lastEdited}</td>
                          <td>
                            <span className={STATUS_BADGE[a.status] || 'phil-badge phil-badge-draft'}>{a.status}</span>
                            {isArchived(a) && <span className="phil-badge phil-badge-draft" style={{ marginLeft: 4 }}>Archived</span>}
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{a.industry}</td>
                          <td style={{ color: '#666' }}>{fmtScore(a.avgScore)}</td>
                          <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{a.dateCompleted}</td>
                          <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{a.lastEdited}</td>
                          <td><span className={OUTCOME_BADGE[a.outcome] || 'phil-badge phil-badge-draft'}>{a.outcome}</span></td>
                          <td>
                            <span className={STATUS_BADGE[a.status] || 'phil-badge phil-badge-draft'}>{a.status}</span>
                            {isArchived(a) && <span className="phil-badge phil-badge-draft" style={{ marginLeft: 4 }}>Archived</span>}
                          </td>
                        </>
                      )}
                      <td onClick={e => { e.stopPropagation(); setEditing(a); }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', cursor: 'pointer' }} title="Edit assigned FIs">
                          {assigned.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                              {assigned.map(fi => (
                                <span key={fi.id} style={{
                                  background: '#eef2fb', color: '#1a4fa0', border: '1px solid #d3ddf2',
                                  borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                                }}>{fi.shortName}</span>
                              ))}
                            </div>
                          )}
                          <span style={{ color: '#bbb', fontSize: 12 }}>+ Assign</span>
                        </div>
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        <RowActionMenu
                          open={rowMenu === a.id}
                          archived={isArchived(a)}
                          onToggle={() => setRowMenu(rowMenu === a.id ? null : a.id)}
                          onArchive={() => toggleArchived(a, true)}
                          onUnarchive={() => toggleArchived(a, false)}
                          onReset={() => resetApplication(a)}
                          onDelete={() => deleteAccount(a)}
                        />
                        <span className="phil-arrow" style={{ marginLeft: 6 }}>→</span>
                      </td>
                    </tr>
                    );
                  })}
                  {shown.length === 0 && (
                    <tr><td colSpan={colCount} style={{ textAlign: 'center', color: '#aaa', padding: 28 }}>No matching applications</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {editing && (
        <AssignmentModal
          row={editing}
          fis={(fis ?? []).map((f: any) => ({ id: f.id, shortName: f.shortName || f.name, name: f.name, industry: f.industry }))}
          current={assignedOf(editing).map(f => f.id)}
          onClose={() => setEditing(null)}
          onSaved={(appId, assignedFis) => {
            setOverrides(prev => ({ ...prev, [appId]: assignedFis }));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function SortTh({ label, sortKey, current, dir, onSort }: {
  label: string;
  sortKey: string;
  current: string | null;
  dir: 'asc' | 'desc';
  onSort: (key: string) => void;
}) {
  const active = current === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.25, fontSize: 10 }}>
        {active && dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  );
}

// Per-row ⋯ menu with the Archive / Unarchive, Reset and Delete-account actions.
function RowActionMenu({ open, archived, onToggle, onArchive, onUnarchive, onReset, onDelete }: {
  open: boolean;
  archived: boolean;
  onToggle: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onReset: () => void;
  onDelete: () => void;
}) {
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={onToggle}
        title="Actions"
        style={{
          border: 'none', background: 'transparent', cursor: 'pointer',
          fontSize: 18, lineHeight: 1, color: '#888', padding: '0 4px',
        }}
      >
        ⋯
      </button>
      {open && (
        <>
          {/* Click-catcher to dismiss the menu when clicking elsewhere. */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={onToggle} />
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 21,
            background: '#fff', border: '1px solid #e2e2e2', borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)', minWidth: 140, padding: 4,
          }}>
            {archived ? (
              <button className="phil-filter-opt" style={filterOptStyle(false)} onClick={onUnarchive}>Unarchive</button>
            ) : (
              <button className="phil-filter-opt" style={filterOptStyle(false)} onClick={onArchive}>Archive</button>
            )}
            <button
              className="phil-filter-opt"
              style={{ ...filterOptStyle(false), color: '#c0392b' }}
              onClick={onReset}
            >
              Reset application
            </button>
            <button
              className="phil-filter-opt"
              style={{ ...filterOptStyle(false), color: '#c0392b' }}
              onClick={onDelete}
            >
              Delete account
            </button>
          </div>
        </>
      )}
    </span>
  );
}

// Button + popover that filters the table by a single column value.
function FilterDropdown({ label, labelPlural, value, options, open, onToggle, onSelect }: {
  label: string;
  labelPlural: string;
  value: string;
  options: string[];
  open: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <button
        className={`phil-btn phil-btn-sm ${value ? 'phil-btn-primary' : 'phil-btn-secondary'}`}
        onClick={onToggle}
      >
        {value || label} ▾
      </button>
      {open && (
        <>
          {/* Click-catcher to dismiss the menu when clicking elsewhere. */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={onToggle} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 21,
            background: '#fff', border: '1px solid #e2e2e2', borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)', minWidth: 180, maxHeight: 280,
            overflowY: 'auto', padding: 4,
          }}>
            <button className="phil-filter-opt" onClick={() => onSelect('')}
              style={filterOptStyle(value === '')}>All {labelPlural}</button>
            {options.map(opt => (
              <button key={opt} className="phil-filter-opt" onClick={() => onSelect(opt)}
                style={filterOptStyle(value === opt)}>{opt}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function filterOptStyle(active: boolean): React.CSSProperties {
  return {
    display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
    border: 'none', borderRadius: 6, background: active ? '#eef2fb' : 'transparent',
    color: active ? '#1a4fa0' : '#333', fontWeight: active ? 700 : 500,
    fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
  };
}

interface FiOption { id: string; shortName: string; name: string; industry?: string; }

function AssignmentModal({ row, fis, current, onClose, onSaved }: {
  row: PipelineRow;
  fis: FiOption[];
  current: string[];
  onClose: () => void;
  onSaved: (appId: string, assignedFis: AssignedFi[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(current));
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const shownFis = fis.filter(f =>
    !filter ||
    f.name.toLowerCase().includes(filter.toLowerCase()) ||
    f.shortName.toLowerCase().includes(filter.toLowerCase()),
  );

  const save = () => {
    setSaving(true);
    setError(null);
    API.setApplicationAssignments(row.id, Array.from(selected))
      .then(res => onSaved(row.id, res?.assignedFis ?? []))
      .catch(err => { setError(err?.message ?? 'Save failed'); setSaving(false); });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 10, width: 440, maxWidth: '90vw', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Assign FIs</div>
          <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>
            {row.company} · <span style={{ fontFamily: 'monospace' }}>{row.id}</span>
          </div>
        </div>

        <div style={{ padding: '12px 20px 0' }}>
          <input
            className="search-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            placeholder="🔍 Filter FIs…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </div>

        <div style={{ overflowY: 'auto', padding: '10px 20px', flex: 1 }}>
          {shownFis.length === 0 && <div style={{ color: '#aaa', padding: 16, textAlign: 'center' }}>No FIs found</div>}
          {shownFis.map(f => (
            <label key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} />
              <span style={{ fontWeight: 600 }}>{f.shortName}</span>
              {f.industry && <span style={{ color: '#aaa', fontSize: 12 }}>· {f.industry}</span>}
            </label>
          ))}
        </div>

        {error && <div className="phil-error" style={{ margin: '0 20px' }}>{error}</div>}

        <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#888', fontSize: 13 }}>{selected.size} selected</span>
          <button className="phil-btn phil-btn-secondary phil-btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={saving}>Cancel</button>
          <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
